/**
 * Opakované udalosti — podmnožina RFC 5545 RRULE, ktorú CMS vie vyrobiť aj
 * prečítať (FREQ daily/weekly/monthly/yearly, INTERVAL 1–2, jeden BYDAY,
 * BYMONTHDAY, UNTIL, COUNT). Zdieľané API (expanzia výskytov) + admin/mobil
 * (presety, popisy). Bez externej závislosti; pásmo cez Intl.DateTimeFormat.
 */
export type RecurrenceFreq = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY'
export type RecurrenceInterval = 1 | 2

export interface RecurrenceInput {
  freq: RecurrenceFreq
  interval?: RecurrenceInterval
  /** Posledný deň opakovania (YYYY-MM-DD, v pásme série). */
  until?: string
  /** Počet výskytov vrátane prvého. */
  count?: number
}

/** Súhrn série vracaný API pri každom výskyte. */
export interface EventRecurrenceInfo extends RecurrenceInput {
  label: string
}

export type RecurrenceScope = 'this' | 'following' | 'all'
export const RECURRENCE_SCOPES: RecurrenceScope[] = ['this', 'following', 'all']
export const RECURRENCE_SCOPE_LABELS: Record<RecurrenceScope, string> = {
  this: 'Len táto udalosť',
  following: 'Táto a nasledujúce',
  all: 'Všetky udalosti série',
}

export interface RecurrencePreset { key: string; label: string; freq: RecurrenceFreq; interval: RecurrenceInterval }
export const RECURRENCE_PRESETS: RecurrencePreset[] = [
  { key: 'DAILY', label: 'Denne', freq: 'DAILY', interval: 1 },
  { key: 'WEEKLY', label: 'Týždenne', freq: 'WEEKLY', interval: 1 },
  { key: 'BIWEEKLY', label: 'Každé 2 týždne', freq: 'WEEKLY', interval: 2 },
  { key: 'MONTHLY', label: 'Mesačne', freq: 'MONTHLY', interval: 1 },
  { key: 'YEARLY', label: 'Ročne', freq: 'YEARLY', interval: 1 },
]

export function recurrencePresetKey(r: Pick<RecurrenceInput, 'freq' | 'interval'> | null | undefined): string {
  if (!r) return ''
  const p = RECURRENCE_PRESETS.find((x) => x.freq === r.freq && x.interval === (r.interval ?? 1))
  return p?.key ?? ''
}

/** Maximálny počet výskytov na jednu generáciu (create, regenerácia, cron). */
export const RECURRENCE_MAX_OCCURRENCES = 400
/** Horizont materializácie sérií bez konca (mesiace). */
export const RECURRENCE_HORIZON_MONTHS = 12
export const DEFAULT_RECURRENCE_TIMEZONE = 'Europe/Bratislava'

const BYDAY = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const
const FREQS: RecurrenceFreq[] = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY']

export interface LocalParts { y: number; m: number; d: number; hh: number; mm: number; ss: number; wd: number }

const dtfCache = new Map<string, Intl.DateTimeFormat>()
// Cache drží len kanonické názvy: Intl berie pásmo v ľubovoľnej veľkosti písmen, takže kľúč podľa vstupu
// by nechal klienta (`?timeZone=`) donekonečna rozširovať mapu.
function dtf(timeZone: string): Intl.DateTimeFormat {
  const direct = dtfCache.get(timeZone)
  if (direct) return direct
  const f = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    weekday: 'short',
  })
  const canonical = f.resolvedOptions().timeZone
  const cached = dtfCache.get(canonical)
  if (cached) return cached
  dtfCache.set(canonical, f)
  return f
}

export function isValidTimezone(tz: string): boolean {
  try {
    dtf(tz)
    return true
  } catch {
    return false
  }
}

const WD_MAP: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }

/** Lokálne časti dátumu v danom pásme. */
export function toLocalParts(date: Date, timeZone: string): LocalParts {
  const parts = dtf(timeZone).formatToParts(date)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
  // Niektoré runtime vracajú hour "24" pre polnoc aj s h23 — normalizuj.
  const hh = Number(get('hour')) % 24
  return {
    y: Number(get('year')), m: Number(get('month')), d: Number(get('day')),
    hh, mm: Number(get('minute')), ss: Number(get('second')),
    wd: WD_MAP[get('weekday')] ?? 0,
  }
}

/** Lokálny čas (časti v pásme) → UTC Date. Pri neexistujúcom čase (jarný skok) posunie dopredu. */
export function fromLocalParts(p: Omit<LocalParts, 'wd'>, timeZone: string): Date {
  const asUtc = Date.UTC(p.y, p.m - 1, p.d, p.hh, p.mm, p.ss)
  // Offset sa odhadne v dvoch iteráciách (stačí pre všetky reálne pásma).
  let guess = asUtc
  for (let i = 0; i < 2; i++) {
    const lp = toLocalParts(new Date(guess), timeZone)
    const local = Date.UTC(lp.y, lp.m - 1, lp.d, lp.hh, lp.mm, lp.ss)
    guess += asUtc - local
  }
  return new Date(guess)
}

function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate()
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

function ymdOf(p: { y: number; m: number; d: number }): string {
  return `${p.y}-${pad2(p.m)}-${pad2(p.d)}`
}

function fmtUtcCompact(d: Date): string {
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z')
}

/** `until` (YYYY-MM-DD) → koniec dňa v pásme ako UTC. */
export function untilDateToUtc(until: string, timeZone: string): Date {
  const [y = 0, m = 1, d = 1] = until.split('-').map(Number)
  return fromLocalParts({ y, m, d, hh: 23, mm: 59, ss: 59 }, timeZone)
}

export function buildRrule(input: RecurrenceInput, dtstart: Date, timeZone: string): string {
  const interval = input.interval ?? 1
  const lp = toLocalParts(dtstart, timeZone)
  const parts = [`FREQ=${input.freq}`]
  if (interval !== 1) parts.push(`INTERVAL=${interval}`)
  if (input.freq === 'WEEKLY') parts.push(`BYDAY=${BYDAY[lp.wd]}`)
  if (input.freq === 'MONTHLY') parts.push(`BYMONTHDAY=${lp.d}`)
  if (input.until) parts.push(`UNTIL=${fmtUtcCompact(untilDateToUtc(input.until, timeZone))}`)
  if (input.count) parts.push(`COUNT=${input.count}`)
  return parts.join(';')
}

export interface ParsedRrule extends RecurrenceInput {
  /** UTC hranica z UNTIL (inkluzívna). */
  untilAt?: Date
  byday?: number
  bymonthday?: number
}

/** Interné parsovanie (aj BYDAY/BYMONTHDAY/untilAt). Vyhodí Error pri nepodporovanom pravidle. */
export function parseRruleFull(rrule: string, timeZone: string): ParsedRrule {
  const kv = new Map<string, string>()
  for (const seg of rrule.split(';').filter(Boolean)) {
    const eq = seg.indexOf('=')
    if (eq <= 0) throw new Error(`Neplatná časť RRULE: ${seg}`)
    kv.set(seg.slice(0, eq).toUpperCase(), seg.slice(eq + 1))
  }
  const freq = kv.get('FREQ') as RecurrenceFreq | undefined
  if (!freq || !FREQS.includes(freq)) throw new Error('Nepodporovaná frekvencia')
  const interval = Number(kv.get('INTERVAL') ?? 1)
  if (interval !== 1 && interval !== 2) throw new Error('Nepodporovaný interval')
  const allowed = new Set(['FREQ', 'INTERVAL', 'BYDAY', 'BYMONTHDAY', 'UNTIL', 'COUNT'])
  for (const k of kv.keys()) if (!allowed.has(k)) throw new Error(`Nepodporovaná časť RRULE: ${k}`)

  const out: ParsedRrule = { freq, interval: interval as RecurrenceInterval }

  const byday = kv.get('BYDAY')
  if (byday) {
    if (freq !== 'WEEKLY' || byday.includes(',')) throw new Error('BYDAY je podporované len pre WEEKLY s jedným dňom')
    const idx = BYDAY.indexOf(byday as (typeof BYDAY)[number])
    if (idx < 0) throw new Error('Neplatný BYDAY')
    out.byday = idx
  }
  const bymonthday = kv.get('BYMONTHDAY')
  if (bymonthday) {
    if (freq !== 'MONTHLY' || bymonthday.includes(',')) throw new Error('BYMONTHDAY je podporované len pre MONTHLY s jedným dňom')
    const n = Number(bymonthday)
    if (!Number.isInteger(n) || n < 1 || n > 31) throw new Error('Neplatný BYMONTHDAY')
    out.bymonthday = n
  }
  const until = kv.get('UNTIL')
  if (until) {
    const m = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})Z?)?$/.exec(until)
    if (!m) throw new Error('Neplatný UNTIL')
    const [, yy = '', mo = '', dd = '', hh, mi = '0', ss = '0'] = m
    const at = hh !== undefined
      ? new Date(Date.UTC(+yy, +mo - 1, +dd, +hh, +mi, +ss))
      : fromLocalParts({ y: +yy, m: +mo, d: +dd, hh: 23, mm: 59, ss: 59 }, timeZone)
    if (Number.isNaN(at.getTime())) throw new Error('Neplatný UNTIL')
    out.untilAt = at
    out.until = ymdOf(toLocalParts(at, timeZone))
  }
  const count = kv.get('COUNT')
  if (count) {
    const n = Number(count)
    if (!Number.isInteger(n) || n < 1) throw new Error('Neplatný COUNT')
    out.count = n
  }
  if (out.until && out.count) throw new Error('UNTIL a COUNT sa vylučujú')
  return out
}

/** Verejný parse — vráti len používateľské polia (bez interných BYDAY/BYMONTHDAY). */
export function parseRrule(rrule: string, timeZone = DEFAULT_RECURRENCE_TIMEZONE): RecurrenceInput {
  const p = parseRruleFull(rrule, timeZone)
  const out: RecurrenceInput = { freq: p.freq, interval: p.interval ?? 1 }
  if (p.until) out.until = p.until
  if (p.count) out.count = p.count
  return out
}

function fmtSkDate(ymd: string): string {
  const [y = 0, m = 0, d = 0] = ymd.split('-').map(Number)
  return `${d}. ${m}. ${y}`
}

export function describeRecurrence(r: RecurrenceInput): string {
  const interval = r.interval ?? 1
  const base: Record<RecurrenceFreq, [string, string]> = {
    DAILY: ['Denne', 'Každé 2 dni'],
    WEEKLY: ['Týždenne', 'Každé 2 týždne'],
    MONTHLY: ['Mesačne', 'Každé 2 mesiace'],
    YEARLY: ['Ročne', 'Každé 2 roky'],
  }
  let s = base[r.freq][interval === 2 ? 1 : 0]
  if (r.until) s += ` · do ${fmtSkDate(r.until)}`
  else if (r.count) s += ` · ${r.count}×`
  return s
}

export interface ExpandOptions {
  /** Vynechá výskyty do tejto hranice vrátane (exkluzívne od). */
  from?: Date
  /** Horná hranica (inkluzívne). */
  until: Date
  /** Tvrdý strop počtu vrátených výskytov. */
  max: number
  /** Celodenná séria: krok v UTC dňoch, bez pásma. */
  allDay?: boolean
}

const MAX_ITER = 100_000

/**
 * Výskyty série od kotvy `dtstart` (kotva je prvý výskyt, ak vyhovuje `from`).
 * COUNT sa počíta od kotvy (vrátane), aj keď `from` niektoré výskyty vynechá.
 */
export function expandOccurrences(rrule: string, dtstart: Date, timeZone: string, opts: ExpandOptions): Date[] {
  const rule = parseRruleFull(rrule, timeZone)
  const interval = rule.interval ?? 1
  const tz = opts.allDay ? 'UTC' : timeZone
  const anchor = toLocalParts(dtstart, tz)
  const untilAt = rule.untilAt && rule.untilAt < opts.until ? rule.untilAt : opts.until
  const out: Date[] = []
  let produced = 0 // vrátane výskytov pred `from` (COUNT sémantika)

  /** Vráti false, keď má expanzia skončiť. */
  const push = (d: Date): boolean => {
    if (d.getTime() > untilAt.getTime()) return false
    produced++
    if (rule.count && produced > rule.count) return false
    if (!opts.from || d.getTime() > opts.from.getTime()) out.push(d)
    return out.length < opts.max
  }
  const at = (y: number, m: number, d: number) => fromLocalParts({ y, m, d, hh: anchor.hh, mm: anchor.mm, ss: anchor.ss }, tz)

  if (rule.freq === 'DAILY' || rule.freq === 'WEEKLY') {
    const stepDays = rule.freq === 'DAILY' ? interval : 7 * interval
    // Krok v lokálnych kalendárnych dňoch: čas ostáva lokálny (DST-safe).
    for (let i = 0; i < MAX_ITER; i++) {
      const base = new Date(Date.UTC(anchor.y, anchor.m - 1, anchor.d + i * stepDays))
      if (!push(at(base.getUTCFullYear(), base.getUTCMonth() + 1, base.getUTCDate()))) break
    }
    return out
  }

  if (rule.freq === 'MONTHLY') {
    const day = rule.bymonthday ?? anchor.d
    for (let i = 0; i < MAX_ITER; i++) {
      const total = anchor.m - 1 + i * interval
      const y = anchor.y + Math.floor(total / 12)
      const m = (total % 12) + 1
      if (day > daysInMonth(y, m)) {
        // Mesiac bez tohto dňa sa preskočí (RFC 5545); hranica until ukončí slučku cez prvý deň mesiaca.
        if (at(y, m, 1).getTime() > untilAt.getTime()) break
        continue
      }
      if (!push(at(y, m, day))) break
    }
    return out
  }

  // YEARLY
  for (let i = 0; i < MAX_ITER; i++) {
    const y = anchor.y + i * interval
    if (anchor.d > daysInMonth(y, anchor.m)) {
      if (at(y, anchor.m, 1).getTime() > untilAt.getTime()) break
      continue
    }
    if (!push(at(y, anchor.m, anchor.d))) break
  }
  return out
}

/** Bezpečný súhrn pre API response (`null` pri nečitateľnom pravidle). */
export function recurrenceInfoFromRrule(rrule: string | null | undefined, timeZone: string): EventRecurrenceInfo | null {
  if (!rrule) return null
  try {
    const r = parseRrule(rrule, timeZone)
    return { ...r, label: describeRecurrence(r) }
  } catch {
    return null
  }
}
